import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface GrowthMetric {
  id: string;
  name: string;
  description: string;
  growth_pillar_id: string;
  category: 'North Star' | 'Supportive Metric';
  configured: boolean;
  configuration_details: string;
  aggregate?: string;
  created_at: string;
  updated_at: string;
}

interface CreateGrowthMetricData {
  name: string;
  description: string;
  growth_pillar_id: string;
  brand_id: string;
  category: 'North Star' | 'Supportive Metric';
  configured?: boolean;
  configuration_details?: string;
  aggregate?: string;
}

interface UpdateGrowthMetricData {
  name: string;
  description: string;
  category: 'North Star' | 'Supportive Metric';
  configured?: boolean;
  configuration_details?: string;
  aggregate?: string;
}

export const useGrowthMetrics = (growthPillarId?: string) => {
  const [growthMetrics, setGrowthMetrics] = useState<GrowthMetric[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchGrowthMetrics = async () => {
    if (!user || !growthPillarId) {
      setGrowthMetrics([]);
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('growth_metrics')
        .select('*')
        .eq('growth_pillar_id', growthPillarId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Type assertion to ensure category matches our union type
      const typedData = (data || []).map(item => ({
        ...item,
        category: item.category as 'North Star' | 'Supportive Metric'
      }));
      
      // Sort so North Star metrics appear first
      const sortedData = typedData.sort((a, b) => {
        if (a.category === 'North Star' && b.category !== 'North Star') return -1;
        if (a.category !== 'North Star' && b.category === 'North Star') return 1;
        return 0;
      });
      
      setGrowthMetrics(sortedData);
    } catch (error: any) {
      console.error('Error fetching growth metrics:', error);
      toast({
        title: "Error",
        description: "Failed to fetch growth metrics",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createGrowthMetric = async (data: CreateGrowthMetricData): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Error",
        description: "Please log in first",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_metrics')
        .insert([data]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Growth metric created successfully",
      });

      await fetchGrowthMetrics();
      return true;
    } catch (error: any) {
      console.error('Error creating growth metric:', error);
      toast({
        title: "Error",
        description: "Failed to create growth metric",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateGrowthMetric = async (id: string, data: UpdateGrowthMetricData): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_metrics')
        .update(data)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Growth metric updated successfully",
      });

      await fetchGrowthMetrics();
      return true;
    } catch (error: any) {
      console.error('Error updating growth metric:', error);
      toast({
        title: "Error",
        description: "Failed to update growth metric",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteGrowthMetric = async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('growth_metrics')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Growth metric deleted successfully",
      });

      await fetchGrowthMetrics();
      return true;
    } catch (error: any) {
      console.error('Error deleting growth metric:', error);
      toast({
        title: "Error",
        description: "Failed to delete growth metric",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGrowthMetrics();
  }, [user, growthPillarId]);

  return {
    growthMetrics,
    isLoading,
    createGrowthMetric,
    updateGrowthMetric,
    deleteGrowthMetric,
    refetch: fetchGrowthMetrics,
  };
};
